import { useState, useCallback, useEffect } from 'react';
import { useWebSocket } from '@/utils/contexts/WebSocketContext';

interface UseAdminHeaderNotificationsReturn {
  showNotification: boolean;
  notificationMessage: string;
  handleReload: () => void;
  dismissNotification: () => void;
}

const COOLDOWN_DURATION = 10 * 60 * 1000; // 10 minutes in milliseconds
const COOLDOWN_KEY = 'admin_video_popup_cooldown';

const useAdminHeaderNotifications = (
  _userEmail: string | null,
  isAdmin: boolean
): UseAdminHeaderNotificationsReturn => {
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [isInCooldown, setIsInCooldown] = useState(false);
  
  const { subscribe, unsubscribe } = useWebSocket();

  // Check cooldown status on mount
  useEffect(() => {
    const cooldownEnd = localStorage.getItem(COOLDOWN_KEY);
    if (cooldownEnd) {
      const now = Date.now();
      const cooldownEndTime = Number.parseInt(cooldownEnd, 10);
      if (now < cooldownEndTime) {
        setIsInCooldown(true);
        const remainingTime = cooldownEndTime - now;
        setTimeout(() => {
          setIsInCooldown(false);
          localStorage.removeItem(COOLDOWN_KEY);
        }, remainingTime);
      } else {
        localStorage.removeItem(COOLDOWN_KEY);
      }
    }
  }, []);

  // Handle admin popup notifications from WebSocket
  useEffect(() => {
    if (!isAdmin || isInCooldown) return;

    const handleMessage = (message: any) => {
      try {
        // Only show popup for admin video popup events
        if (
          message.event === 'admin_video_popup' &&
          message.data &&
          (message.data.type === 'uploaded' || message.data.type === 'generated')
        ) {
          setNotificationMessage('Latest videos');
          setShowNotification(true);
        }
      } catch (error) {
        console.error('Error handling admin popup message:', error);
      }
    };

    const componentId = 'admin-header-notifications';
    subscribe(componentId, handleMessage);

    return () => {
      unsubscribe(componentId);
    };
  }, [isAdmin, isInCooldown, subscribe, unsubscribe]);

  // Handle reload with cooldown
  const handleReload = useCallback(() => {
    // Set cooldown
    const cooldownEnd = Date.now() + COOLDOWN_DURATION;
    localStorage.setItem(COOLDOWN_KEY, cooldownEnd.toString());
    setIsInCooldown(true);

    // Remove cooldown after duration
    setTimeout(() => {
      setIsInCooldown(false);
      localStorage.removeItem(COOLDOWN_KEY);
    }, COOLDOWN_DURATION);

    // Hide notification and reload
    setShowNotification(false);
    globalThis.location.reload();
  }, []);

  // Dismiss notification
  const dismissNotification = useCallback(() => {
    setShowNotification(false);
  }, []);

  return {
    showNotification,
    notificationMessage,
    handleReload,
    dismissNotification
  };
};

export default useAdminHeaderNotifications;